import { Button } from '@/components/ui/button';
import { useContentDetail } from '@/hooks/useContentQueries';
import { OBSERVATION_CAPTURE_LABELS } from '@/components/widget/observation-capture/labels';
import { VideoPlayer } from '@/features/viewers/Video';
import { ChevronUp, ExternalLink, Link2, MoreHorizontal, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { resolveLinkSeed } from './provenance-link-seed';

export interface ProvenanceVideoListItem {
  id: string;
  name: string;
  remoteId?: number;
}

export interface ProvenanceVideoListProps {
  videos: ProvenanceVideoListItem[];
  draftText?: string;
  onAddUnlinked?: () => void;
  onAddLinked?: (linkContentId?: number) => void;
  onRemove?: (videoId: string) => void;
  onOpenContent?: (contentId: number) => void;
}

function VideoItem({
  video,
  onRemove,
  onOpenContent,
}: {
  video: ProvenanceVideoListItem;
  onRemove?: (id: string) => void;
  onOpenContent?: (contentId: number) => void;
}) {
  const detailQuery = useContentDetail(video.remoteId ?? 0);
  const detail = detailQuery.data;
  const remoteId = video.remoteId;
  const isRemoteVideo = detail?.contentType === 'VIDEO';
  const title =
    typeof detail?.title === 'string' && detail.title.trim().length > 0
      ? detail.title.trim()
      : video.name;
  // A video is stored as-is: nothing is generated for it, so the member's description is
  // the only body text there will ever be and there is no placeholder to wait on.
  const description =
    typeof detail?.description === 'string' && detail.description.trim().length > 0
      ? detail.description.trim()
      : null;
  const [expanded, setExpanded] = useState(false);
  const videoUrl =
    isRemoteVideo && typeof detail.uri === 'string' && detail.uri.trim().length > 0
      ? `/api/files/video/${detail.uri}`
      : undefined;

  return (
    <div className="rounded border p-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-xs">{title}</p>
        <div className="flex items-center gap-1">
          {typeof remoteId === 'number' && onOpenContent ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Open video"
              title="Open video"
              onClick={() => onOpenContent(remoteId)}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onRemove(video.id)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {videoUrl ? <VideoPlayer src={videoUrl} /> : null}

      {description ? (
        <div className="flex items-start gap-1">
          <p className={`text-xs text-muted-foreground flex-1 ${expanded ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {description}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setExpanded((previous) => !previous)}
            title={expanded ? 'Show less' : 'Show more'}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function ProvenanceVideoList({
  videos,
  draftText = '',
  onAddUnlinked,
  onAddLinked,
  onRemove,
  onOpenContent,
}: ProvenanceVideoListProps) {
  if (!draftText.trim() && videos.length === 0) return null;

  // This list also renders on draftText alone, so a zero-video card is reachable —
  // Add Linked needs something to link to and is hidden when there is nothing.
  const linkSeed = resolveLinkSeed(videos);

  return (
    <div className="py-2 text-xs bg-muted/30 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">Videos</p>
        <div className="flex items-center gap-2">
          {onAddUnlinked ? (
            <Button variant="outline" size="sm" onClick={onAddUnlinked}>
              <Plus className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addUnlinked}
            </Button>
          ) : null}
          {onAddLinked && linkSeed !== undefined ? (
            <Button variant="outline" size="sm" onClick={() => onAddLinked(linkSeed)}>
              <Link2 className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addLinked}
            </Button>
          ) : null}
        </div>
      </div>
      {videos.length > 0 ? (
        <div className="space-y-2">
          {videos.map((video) => (
            <VideoItem
              key={video.id}
              video={video}
              onRemove={onRemove}
              onOpenContent={onOpenContent}
            />
          ))}
        </div>
      ) : null}
      {draftText.trim() ? (
        <p className="text-muted-foreground">{draftText.trim().length} characters of typed notes</p>
      ) : null}
    </div>
  );
}
